// Templates — three sub-tabs covering every send template the system uses.
//
//   Outreach  — cold creator outreach (buildOutreachDraft). Body shown with
//               {placeholders} so the structure is auditable here.
//   Engagement — the 7 drop-off lifecycle templates from @stax/shared. Same
//               renderer used by the daily 13:00 UTC engagement cron.
//   Auto-Reply — reply-pipeline templates from ass.response_templates,
//               keyed by intent (interested / not_now / ooo).
//
// Restored 2026-05-20 after the engagement templates were only visible on
// /engagement and felt "lost" from /templates. This page is now the single
// source-of-truth view of every template family.

import { useMemo, useState } from 'react';
import {
  DROPOFF_STAGE_IDS,
  DROPOFF_STAGES,
  PERF_CHART_PLACEHOLDER,
  RECENT_CHANGES_DEFAULT,
  renderDropoffEmail,
  type DropoffStageId,
} from '@stax/shared';
import { useTemplates, useSenderIdentities, type ResponseTemplate, type SenderIdentity } from '../lib/queries';
import { IntentBadge } from '../components/IntentBadge';
import type { ReplyIntent } from '@stax/shared';

type TabId = 'outreach' | 'engagement' | 'auto-reply';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'outreach', label: 'Outreach' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'auto-reply', label: 'Auto-Reply' },
];

export function Templates() {
  const [tab, setTab] = useState<TabId>('outreach');

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section 05</span>
          <span>Templates</span>
          <span>{TABS.length} families</span>
        </div>
        <h1 className="page__title">Templates</h1>
        <p className="page__deck">
          Every outbound and reply-side template the system uses, organized by family.
          Drop-off / lifecycle drafts continue to render and send from this single source.
        </p>
      </header>

      <nav className="tpl-tabs" aria-label="Template families">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tpl-tab ${tab === t.id ? 'tpl-tab--active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'outreach' && <OutreachTab />}
      {tab === 'engagement' && <EngagementTab />}
      {tab === 'auto-reply' && <AutoReplyTab />}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Outreach — cold creator outreach. The body below is the literal template
// produced by buildOutreachDraft in app/apps/worker/src/services/draft-template.ts.
// {placeholders} mark the only varying slots; everything else is byte-locked.

const OUTREACH_SUBJECT = 'Potential partnership with Stax Labs';
const OUTREACH_FROM = 'Stax Labs <hello@updates.staxlabs.org>';
const OUTREACH_REPLY_TO = 'bianchi.luc9@gmail.com, pedro@staxlabs.org, hartmantexas@gmail.com';
const OUTREACH_BCC = 'bianchi.luc9@gmail.com';
const OUTREACH_BODY = `Hi {first_name},

My name is Luca Bianchi, and I recently graduated from Colgate University and now work at both Accenture and Stax Labs. I found your {platform_noun} and thoroughly enjoyed your content, {personalized_clause}.

I wanted to discuss a potential partnership between you and Stax Labs. We help users develop algorithms to meet their financial goals by removing the emotional aspect from investing. We've developed an optimization engine that searches 233 quadrillion possible combinations of user strategies to find pockets of certainties.

With your expertise and platform, we'd really appreciate your help stress testing the platform to ensure it's a useful investment tool. I'd be happy to set up some time to discuss what the partnership might look like.

Thank you,

Luca

staxlabs.org`;

function OutreachTab() {
  return (
    <section className="tpl-section">
      <header className="tpl-section-head">
        <h2 className="tpl-section-title">Cold outreach</h2>
        <p className="tpl-section-sub">
          Sent to every newly-discovered creator with a verified email. Sanitized via
          firstName() and sanitizeNiche() at write time, validated again at send time,
          locked by a DB trigger on ass.tracker.message_draft so a bad draft can never land.
        </p>
      </header>

      <dl className="tpl-meta">
        <div><dt>Subject</dt><dd>{OUTREACH_SUBJECT}</dd></div>
        <div><dt>From</dt><dd>{OUTREACH_FROM}</dd></div>
        <div><dt>Reply-to</dt><dd>{OUTREACH_REPLY_TO}</dd></div>
        <div><dt>BCC</dt><dd>{OUTREACH_BCC}</dd></div>
        <div><dt>Frequency</dt><dd>Daily at 14:30 UTC (~100/day cap)</dd></div>
      </dl>

      <h3 className="tpl-body-head">Body (placeholders in <code>{'{}'}</code>)</h3>
      <pre className="tpl-body">{OUTREACH_BODY}</pre>

      <h3 className="tpl-body-head">Personalized clause variants</h3>
      <ul className="tpl-variants">
        <li>
          <strong>Specific video known:</strong>
          {' '}
          <code>specifically your video "X"</code>
        </li>
        <li>
          <strong>Sanitized niche available:</strong>
          {' '}
          <code>specifically your work around {'{niche}'}</code>
        </li>
        <li>
          <strong>Fallback (anything ambiguous):</strong>
          {' '}
          <code>and your insights in particular</code>
        </li>
      </ul>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Engagement — 7 drop-off lifecycle templates rendered via @stax/shared.

const PREVIEW_FIRST_NAME = 'Alex';
const PREVIEW_RETURN = '+6.4%';
const PREVIEW_RANGE = 'this week';
const PREVIEW_REFERRAL_CODE = 'STAX-7K2N-9X3M';

function EngagementTab() {
  const [stageId, setStageId] = useState<DropoffStageId>('approved-no-signup');
  const stage = DROPOFF_STAGES[stageId];
  const rendered = useMemo(
    () =>
      renderDropoffEmail(stageId, {
        firstName: PREVIEW_FIRST_NAME,
        recentChanges: stageId === 'churn-reengage' ? RECENT_CHANGES_DEFAULT : undefined,
        chartImageSrc: stageId === 'performance-snapshot' ? PERF_CHART_PLACEHOLDER : undefined,
        summaryReturn: stageId === 'performance-snapshot' ? PREVIEW_RETURN : undefined,
        summaryRange: stageId === 'performance-snapshot' ? PREVIEW_RANGE : undefined,
        referralCode: stageId === 'signed-up-no-login' ? PREVIEW_REFERRAL_CODE : undefined,
      }),
    [stageId],
  );

  return (
    <section className="tpl-section">
      <header className="tpl-section-head">
        <h2 className="tpl-section-title">Engagement (drop-off / lifecycle)</h2>
        <p className="tpl-section-sub">
          7 templates fired by the daily 13:00 UTC cron based on each user's lifecycle
          stage. Per-stage 365-day dedup (6d for performance-snapshot). A given user
          gets at most one engagement email per sweep across all stages.
        </p>
      </header>

      <nav className="tpl-stage-strip" aria-label="Stage">
        {DROPOFF_STAGE_IDS.map((id, i) => {
          const s = DROPOFF_STAGES[id];
          const active = id === stageId;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setStageId(id)}
              className={`tpl-stage-btn ${active ? 'tpl-stage-btn--active' : ''}`}
            >
              <span className="tpl-stage-num">{String(i + 1).padStart(2, '0')}</span>
              <span className="tpl-stage-kind">{s.kind}</span>
              <span className="tpl-stage-title">{s.title}</span>
            </button>
          );
        })}
      </nav>

      <dl className="tpl-meta">
        <div><dt>Subject</dt><dd>{rendered.subject}</dd></div>
        <div><dt>Trigger</dt><dd>{stage.triggerSummary}</dd></div>
        <div><dt>Default delay</dt><dd>{stage.defaultDelay}</dd></div>
        <div><dt>Headline</dt><dd>{stage.headline}</dd></div>
        <div><dt>Preheader</dt><dd>{stage.preheader}</dd></div>
        <div><dt>CTA</dt><dd>{stage.ctaLabel} → {stage.ctaUrl}</dd></div>
      </dl>

      <div className="tpl-preview-row">
        <div className="tpl-preview-col">
          <div className="tpl-preview-bar">
            <span>Inbox preview</span>
            <span className="tpl-preview-from">from hello@updates.staxlabs.org</span>
          </div>
          <iframe
            key={stageId}
            title={`Preview: ${stage.title}`}
            srcDoc={rendered.html}
            className="tpl-preview-iframe"
            sandbox=""
          />
        </div>
        <div className="tpl-preview-col">
          <div className="tpl-preview-bar"><span>Plain-text fallback</span></div>
          <pre className="tpl-body tpl-body--plain">{rendered.text}</pre>
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Auto-Reply — the existing ass.response_templates, grouped by intent.

function AutoReplyTab() {
  const tplQ = useTemplates();
  const sendersQ = useSenderIdentities();
  const grouped = useMemo(() => {
    const out: Record<string, ResponseTemplate[]> = {};
    for (const t of tplQ.data ?? []) (out[t.intent] ??= []).push(t);
    return out;
  }, [tplQ.data]);
  const senderById = useMemo(() => {
    const m = new Map<string, SenderIdentity>();
    for (const s of sendersQ.data ?? []) m.set(s.id, s);
    return m;
  }, [sendersQ.data]);

  return (
    <section className="tpl-section">
      <header className="tpl-section-head">
        <h2 className="tpl-section-title">Auto-reply templates</h2>
        <p className="tpl-section-sub">
          Used by the reply pipeline to draft auto-responses to classified inbound
          replies. One default per intent. Edit via SQL migrations.
        </p>
      </header>

      {tplQ.isLoading && <p><em>Loading…</em></p>}
      {tplQ.error && (
        <p className="page__deck page__deck--error">
          Couldn't load templates: <code>{tplQ.error.message}</code>
        </p>
      )}

      {Object.entries(grouped).map(([intent, list]) => (
        <section key={intent} className="template-section">
          <h2 className="template-section__head">
            <IntentBadge intent={intent as ReplyIntent} />
            <span className="template-section__count">{list.length}</span>
          </h2>
          <div className="template-section__cards">
            {list.map((t) => {
              const sender = t.sender_identity_id ? senderById.get(t.sender_identity_id) : null;
              return (
                <article key={t.id} className="template-card">
                  <header className="template-card__head">
                    <h3 className="template-card__name">{t.name}</h3>
                    <div className="template-card__meta">
                      {t.is_default_for_intent && <span className="template-card__badge">Default</span>}
                      {!t.active && (
                        <span className="template-card__badge template-card__badge--off">Inactive</span>
                      )}
                      {sender && (
                        <span className="template-card__sender">via {sender.display_name}</span>
                      )}
                    </div>
                  </header>
                  {t.subject_template && (
                    <p className="template-card__subject">
                      <span className="template-card__label">Subject</span>
                      <span>{t.subject_template}</span>
                    </p>
                  )}
                  <pre className="template-card__body">
                    {t.body_template.length > 320
                      ? `${t.body_template.slice(0, 320)}…`
                      : t.body_template}
                  </pre>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </section>
  );
}
